import { Button, Group, Modal, Stack } from '@mantine/core';
import { zodResolver } from '@mantine/form';
import moment from 'moment';
import { z } from 'zod';

import { Document, EnvironmentCountries, EORIGIN_DATE_FORMAT } from '@e-origin/shared';

import { DatePickerInput, EucdmCode, MultiLangCodes, TextInput } from '@components';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { useFormWithNameInputProp } from '@hooks/useFormWithNameInputProp';
import { useSettings } from '@hooks/useSettings';
import { INNER_WHITESPACE_ERROR, isHighValueDeclaration, noInnerWhitespace, trimEdges, trimOnBlurProps } from '@utils';

interface ManageSupportingDocumentViewProps {
  opened: boolean;
  onHide: () => void;
  docDetails?: Document;
  onSave: (savedDocument: Document, originalDocument: Document) => void;
  editDisabled: boolean;
  declarationGroup?: string;
}

const supportingDocumentSchema = z.object({
  code: z.string().trim().min(1, { message: 'Code is required' }).refine(noInnerWhitespace, { message: INNER_WHITESPACE_ERROR }),
  referenceNumber: z.string().trim().min(1, { message: 'Reference is required' }),
  dateOfValidity: z.date().optional(),
});

type SupportingDocumentValues = z.infer<typeof supportingDocumentSchema>;

export const ManageSupportingDocumentView: React.FC<ManageSupportingDocumentViewProps> = (props) => {
  const { opened, onHide, docDetails, onSave, editDisabled, declarationGroup } = props;
  const { data: settings } = useSettings();
  const isNetherlands = settings.country === EnvironmentCountries.NL;
  const isBEHighValue = settings.country === EnvironmentCountries.BE && isHighValueDeclaration(declarationGroup);
  const codelistSystems = useCodeListSystem({});

  const form = useFormWithNameInputProp<SupportingDocumentValues>({
    initialValues: {
      code: docDetails?.type || '',
      referenceNumber: docDetails?.referenceNumber || '',
      dateOfValidity: docDetails?.dateOfValidity ? moment(docDetails?.dateOfValidity, EORIGIN_DATE_FORMAT).toDate() : undefined,
    },
    validate: zodResolver(supportingDocumentSchema),
    validateInputOnBlur: true,
  });

  // the code is trimmed as soon as the user leaves the field, so the cleaned value is what gets validated and saved
  const onSubmit = (values: SupportingDocumentValues) => {
    onSave(
      {
        ...docDetails,
        type: trimEdges(values.code),
        referenceNumber: values.referenceNumber,
        dateOfValidity: values.dateOfValidity ? moment(values.dateOfValidity, EORIGIN_DATE_FORMAT).format(EORIGIN_DATE_FORMAT) : undefined,
      },
      docDetails,
    );
    onHide();
  };

  return (
    <Modal title="Edit additional reference document" opened={opened} onClose={onHide} zIndex={10000} centered size="lg">
      <form
        onSubmit={(event) => {
          event.stopPropagation();
          form.onSubmit(onSubmit)(event);
        }}
      >
        <Stack gap="md" p="md">
          <EucdmCode path="documents.additionalReference.type">
            <MultiLangCodes
              label="Code"
              {...trimOnBlurProps(form, 'code')}
              code="CL380"
              system={codelistSystems}
              disabled={!!docDetails || editDisabled}
              size="lg"
            />
          </EucdmCode>

          <EucdmCode path="documents.additionalReference.referenceNumber">
            <TextInput label="Reference" {...form.getInputProps('referenceNumber')} disabled={editDisabled} size="lg" withAsterisk />
          </EucdmCode>

          {!isNetherlands && !isBEHighValue && <DatePickerInput label="Date of validity" {...form.getInputProps('dateOfValidity')} size="lg" />}
        </Stack>

        <Group justify="flex-end" p="md">
          <Button type="button" onClick={onHide} size="lg" variant="transparent">
            Cancel
          </Button>
          <Button type="submit" variant="filled" size="lg" disabled={editDisabled}>
            Save
          </Button>
        </Group>
      </form>
    </Modal>
  );
};
